import type { ComponentProps, ElementType, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type {
  Arrangement,
  ImageSource,
  ValidTitleTags,
} from '../../../../../types'
import { ButtonLink } from '../../../../ButtonLink'
import { Link } from '../../../../Link'
import { LinksListExpandable } from '../../../../LinkBlock/LinksListExpandable'
import type { AspectRatio } from '../../../../Picture'
import { Picture } from '../../../../Picture'
import { SignupButton } from '../../../../SignupButton'
import { CardContainer } from '../../../BuildingBlocks/CardContainer'
import { ContentType } from '../../../BuildingBlocks/ContentType'
import { DateTime } from '../../../BuildingBlocks/DateTime'
import { ReadingTime } from '../../../BuildingBlocks/ReadingTime'
import { TypographyV2 } from '../../../Foundations/Typography'
import styles from './ContentCard.module.scss'

export const ContentCardV2: FunctionComponent<ContentCardProps> = ({
  primaryLink,
  arrangement = 'default',
  icon: Icon,
  buttonLinkText,
  disclaimerText,
  contentLabel,
  contentLabelLink,
  title,
  titleTag = 'h3',
  description,
  secondaryLinks,
  readTime,
  readTimeText,
  imageRight,
  date,
  ariaLabel,
  imgObj,
  onClickHandler,
  cardContainerId,
  trackingData,
  clampDescText = true,
  buttonLinkAdditionalProps = {},
  legalText,
  isSignUp = false,
}) => {
  if (!title) {
    return null
  }

  const cardAriaLabel = ariaLabel ? ariaLabel : title
  const dateSupplied = date ? new Date(date) : null

  const {
    ariaLabel: linksAriaLabel,
    buttonText = '',
    contentHtmlId = '',
    links = [],
  } = secondaryLinks ?? {}
  const {
    imageAlt,
    imageSources,
    lazyLoad,
    str = '',
    aspectRatioMain = 'one-to-one',
  } = imgObj ?? {}

  const hasIcon = !!Icon

  const isCardHorizontal = arrangement === 'horizontal' ? true : false
  const isCardCenter = arrangement === 'center' ? true : false
  const isCardReverse =
    arrangement === 'horizontal' && imageRight ? true : false

  const isCardALink = buttonLinkText ? false : true
  const setCardContainerLink = isCardALink ? primaryLink : ''
  const hasOneSecondaryLink = secondaryLinks?.links.length === 1 ? true : false
  const aspectRatio = legalText ? 'two-to-one' : aspectRatioMain

  return (
    <CardContainer
      ariaLabel={cardAriaLabel}
      arrangement={arrangement}
      primaryLink={setCardContainerLink}
      onClickHandler={onClickHandler}
      id={cardContainerId}
      trackingData={trackingData}
    >
      <div
        className={classnames(styles.card, {
          [styles.cardHorizontal]: isCardHorizontal,
          [styles.cardCenter]: isCardCenter,
          [styles.cardImageRight]: isCardReverse,
        })}
        data-testid="content-card"
      >
        {imgObj?.str && (
          <div
            className={classnames(styles.cardImg)}
            data-testid="content-card-image"
          >
            <Picture
              alt={imageAlt}
              aspectRatioMain={aspectRatio}
              lazyLoad={lazyLoad}
              sources={imageSources}
              src={str}
            />
          </div>
        )}
        <div className={classnames(styles.cardMain)}>
          {hasIcon && contentLabel && (
            <ContentType
              icon={Icon}
              text={contentLabel}
              data-testid="content-card-contentType"
              link={contentLabelLink}
            />
          )}

          <div className={styles.cardTitle}>
            <TypographyV2
              tag={titleTag}
              textStyle="sb-text-interface-heading-large"
              dataTestId="sb-text-interface-heading-large"
            >
              {title}
            </TypographyV2>
          </div>

          {description && (
            <TypographyV2
              textStyle="sb-text-interface-body-small-regular"
              dataTestId="content-card-description"
              className={classnames(styles.cardDesc, {
                [styles.cardDescClampText]: clampDescText,
              })}
            >
              {description}
            </TypographyV2>
          )}

          {secondaryLinks && (
            <div
              className={styles.cardLinks}
              data-testid="content-card-secondaryLinks"
            >
              {hasOneSecondaryLink ? (
                <Link
                  includeIcon={false}
                  className={styles.cardLinksSingle}
                  href={secondaryLinks.links[0].href}
                >
                  {secondaryLinks.links[0].label}
                </Link>
              ) : (
                <LinksListExpandable
                  button={{
                    ariaLabel: linksAriaLabel,
                    text: buttonText,
                  }}
                  contentHtmlId={contentHtmlId}
                  items={links}
                />
              )}
            </div>
          )}

          <div
            className={classnames(styles.cardFooter, {
              [styles.cardFooterCenter]: isCardCenter,
            })}
          >
            {dateSupplied && (
              <div
                className={styles.cardFooterDate}
                data-testid="content-card-date"
              >
                <DateTime dateInput={dateSupplied} showDateOrTime={'Date'} />
              </div>
            )}
            {readTime && (
              <div
                className={classnames({
                  [styles.cardFooterTime]: !dateSupplied,
                })}
              >
                <ReadingTime
                  readTime={readTime}
                  text={readTimeText}
                  data-testid="content-card-readTime"
                />
              </div>
            )}
          </div>

          {isSignUp && (
            <SignupButton
              button={{
                text: buttonLinkText,
              }}
              caption={disclaimerText}
              className={styles.cardSignUpLink}
            />
          )}

          {buttonLinkText && !isSignUp && (
            <ButtonLink
              appearance="primary"
              href={primaryLink}
              className={styles.cardButtonLink}
              disclaimerText={disclaimerText}
              {...buttonLinkAdditionalProps}
            >
              {buttonLinkText}
            </ButtonLink>
          )}
        </div>
      </div>

      {legalText && (
        <div className={styles.cardLegalText} data-testid="legal-text">
          <TypographyV2 textStyle="sb-text-interface-body-small-regular">
            {legalText}
          </TypographyV2>
        </div>
      )}
    </CardContainer>
  )
}

export type ContentCardProps = {
  /**
   * URL for destination of card
   */
  primaryLink?: string
  /**
   * Arrangement of child elements
   */
  arrangement?: Arrangement
  /**
   * aria-label attribute can be used to define a string that labels the interactive element
   */
  ariaLabel?: string
  /**
   * Button link text
   */
  buttonLinkText?: string
  /**
   * Disclaimer text under button
   */
  disclaimerText?: string
  /**
   * card container id string
   */
  cardContainerId?: string
  /**
   * Clamp description text
   */
  clampDescText?: boolean
  /**
   * content label string
   */
  contentLabel?: string
  /**
   * link for the content label
   */
  contentLabelLink?: string
  /**
   * Date
   */
  date?: Date
  /**
   * description/strapline string
   */
  description?: string
  /**
   * image object
   */
  imgObj?: {
    imageAlt: string
    imageSources?: Sources[]
    lazyLoad?: boolean
    str: string
    aspectRatioMain?: AspectRatio
  } | null
  /**
   * icon string
   */
  icon?: ElementType
  /**
   * Show image on right - only for “arrangement:Horizontal”
   */
  imageRight?: boolean
  /**
   * Function to be passed to on click of onClickHandler component.
   */
  onClickHandler?: () => void
  /**
   * Title string
   */
  title: string
  /**
   * Valid titleTags "h2 "h3" "h4" "h5" "h6"
   */
  titleTag?: ValidTitleTags
  /**
   * Optional data tracking attributes for the primary link
   */
  trackingData?: { [key: string]: string }
  /**
   * Read time of article
   */
  readTime?: string
  /**
   * Read time text which goes next to read time
   */
  readTimeText?: string
  /**
   * Secondary links are optional
   */
  secondaryLinks?: {
    ariaLabel: string
    buttonText: string
    contentHtmlId: string
    links: { href: string; label: string }[]
  } | null
  /**
   * Optional Anchor HTML attributes for the button link ie (target="_blank" rel="noopener")
   */
  buttonLinkAdditionalProps?: ComponentProps<'a'>
  /**
   * Optional Leagal t&cs text field
   */
  legalText?: string
  /**
   * Optional property to render the signup button.
   */
  isSignUp?: boolean
}

type Sources = ImageSource
